import { Link, NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";
import { useT } from "../../contexts/LanguageContext";
import LanguageSwitcher from "./LanguageSwitcher";
import Footer from "./Footer";
import { House, ChartBar, ListChecks, CalendarBlank, GraduationCap, SignOut, Compass, ClockClockwise, ShieldCheck, HandCoins } from "@phosphor-icons/react";

const linkBase = "flex items-center gap-2 px-3 py-2 rounded-md text-sm font-medium transition-colors";

export default function AppShell({ children }) {
  const { user, logout } = useAuth();
  const { t } = useT();
  const navigate = useNavigate();

  const onLogout = async () => {
    await logout();
    navigate("/", { replace: true });
  };

  const items = [
    { to: "/dashboard", label: t("nav.dashboard"), key: "dashboard", icon: House },
    { to: "/practice", label: t("nav.practice"), key: "practice", icon: GraduationCap },
    { to: "/review", label: t("nav.review"), key: "review", icon: ClockClockwise },
    { to: "/mock", label: t("nav.mock"), key: "mock", icon: Compass },
    { to: "/study-plan", label: t("nav.study_plan"), key: "study-plan", icon: CalendarBlank },
    { to: "/tasks", label: t("nav.tasks"), key: "tasks", icon: ListChecks },
    { to: "/progress", label: t("nav.progress"), key: "progress", icon: ChartBar },
  ];

  return (
    <div className="min-h-screen flex flex-col">
      <header className="glass sticky top-0 z-30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          <Link to="/dashboard" className="flex items-center gap-2" data-testid="brand-link">
            <img src="/logo-full.png" alt="PMCoach AI" className="h-10 w-auto" data-testid="brand-logo" />
          </Link>
          <nav className="hidden md:flex items-center gap-1" data-testid="primary-nav">
            {items.map(({ to, label, key, icon: Icon }) => (
              <NavLink
                key={to}
                to={to}
                data-testid={`nav-${key}`}
                className={({ isActive }) =>
                  `${linkBase} ${isActive ? "bg-slate-900 text-white" : "text-slate-700 hover:bg-slate-100"}`
                }
              >
                <Icon size={16} weight="bold" />
                {label}
              </NavLink>
            ))}
          </nav>
          <div className="flex items-center gap-2">
            <LanguageSwitcher />
            {user?.is_admin && (
              <Link to="/admin" className="hidden sm:inline-flex items-center gap-1 text-xs font-mono uppercase tracking-wider text-[#002FA7] hover:bg-[#DBEAFE] px-2 py-1 rounded" data-testid="nav-admin">
                <ShieldCheck size={14} weight="fill" /> {t("nav.admin")}
              </Link>
            )}
            <Link to="/affiliate" className="hidden md:inline-flex items-center gap-1 text-xs font-mono uppercase tracking-wider text-slate-600 hover:text-[#002FA7] hover:bg-slate-100 px-2 py-1 rounded" data-testid="nav-affiliate">
              <HandCoins size={14} weight="fill" /> {t("nav.affiliate")}
            </Link>
            <Link to="/profile" className="text-sm text-slate-600 hover:text-[#002FA7] hidden sm:inline" data-testid="profile-link">
              {user?.name || user?.email}
            </Link>
            <button onClick={onLogout} className="btn-secondary !py-2 !px-3 text-sm" data-testid="logout-button" aria-label={t("common.signout")}>
              <SignOut size={16} weight="bold" />
            </button>
          </div>
        </div>
      </header>
      <main className="flex-1">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">{children}</div>
      </main>
      <Footer />
    </div>
  );
}
